// Hover help for the job page's watch and breach tabs. Meanings follow
// cd-pipeline-infra's docs/guides/auto-rollback-flags.md and
// cd-deployer/internal/rollback/{watcher,decide}.go; keep them in step.

export const WATCH_FIELD_HELP = {
  watchOpened: 'When the auto-rollback watch started polling this deploy. Hover a time for UTC.',
  version: 'The version this deploy replaced, then the version it deployed.',
  mode:
    'The mode the watch ran in: the auto-rollback cell’s mode, or monitor for a deploy whose service is not opted in.',
  endReason: 'Why the watch stopped.',
  highestTier: 'The highest tier any sustained breach reached during the watch. "—" when nothing breached.',
  thresholds: 'The fixed bounds the watch held the deploy to.',
  signals: 'Per signal the watch checked, what it read across the window.',
  polls: 'Polls that read the signal. An under-sampled read is a poll, not a skip.',
  skipped: 'Polls whose read failed.',
  peak: 'The worst reading: the highest, or the lowest for cannot_serve.',
  peakSustained:
    'The worst value held for 3 distinct minutes of consecutive readings. A bound at or below it (above it for cannot_serve) would have breached.',
  sample: 'The smallest and largest denominators read: requests, desired tasks, or error log lines.',
  breachAt: 'When the signal’s breach condition had held long enough to count (UTC).',
  recoveredAt: 'When the signal was next within its bound for 3 distinct minutes (UTC).',
  optedIn: 'Whether the service is opted in to auto-rollback. An opted-out deploy is watched in monitor mode.',
  blockedDeploys: 'Whether the watch held the job lock, blocking other deploys of this job while it ran.',
  skippedRateLimited: 'Datadog reads skipped because the query budget was spent.',
  planHash: 'The hash of the evaluation plan the watch ran, stored on the record as auto_rollback.evaluation_plan.',
  profileVersion: 'The evaluation profile version the plan was resolved from. "—" for the default plan.',
  window: 'How long the watch ran and how often it polled.',
  breach: 'The first sustained breach: the reading the watch recorded and paged on.',
} as const;

export const BREACH_FIELD_HELP = {
  when: 'When the breach was confirmed, or the watch opened if only a case exists. Hover a time for UTC.',
  highestTierOrSignal:
    'From the case: the highest tier the breach reached. From a breach report alone: the signal that breached.',
  action: 'What rollback-decide did or would have done.',
  outcome: 'How the breach ended.',
  rcaCategory: 'The rollback root-cause analysis’ category for the breach.',
  verdict: 'The reviewed verdict on the case. "draft" until someone records one.',
  case: 'The casebook case: the breach, the decision and the RCA in one document.',
  report: 'The breach report the watch wrote, with the readings it breached on.',
} as const;

export const END_REASON_HELP: Record<string, string> = {
  watch_window_elapsed: 'The watch ran its full window.',
  superseded: 'A newer deploy of the same job replaced this one before the window ended.',
  monitor_would_rollback: 'A breach in monitor mode: enforce would have handed this deploy to rollback-decide.',
  evaluated_no_action: 'rollback-decide evaluated the breach and the cell’s mode kept it from acting.',
  evaluation_abandoned: 'The workflow ended while the breach was still being evaluated.',
  kill_switch_off: 'The auto-rollback kill switch was off.',
  kill_switch_read_failed: 'The kill switch could not be read, so the watch stopped (fail closed).',
  rollback_flag_off: 'This deploy type and class’s auto-rollback switch was off.',
  rollback_flag_read_failed: 'The auto-rollback switch could not be read, so the watch stopped (fail closed).',
  opted_out: 'The service is not opted in to auto-rollback.',
  unsupported_deploy_type: 'The watch does not support this deploy type.',
  monitoring_target_unresolved: 'The watch could not work out what to read metrics for.',
  watcher_unhandled_error: 'The watch failed with an unexpected error.',
  rca_failed: 'enforce declined: the rollback RCA did not finish.',
  rca_vetoed: 'enforce declined: the rollback RCA advised against a rollback.',
  rca_low_confidence: 'enforce declined: the RCA’s confidence was below the cell’s min_confidence.',
  min_confidence_unset: 'enforce declined: the cell has no min_confidence.',
  target_not_stable: 'enforce declined: the RCA’s target version is no longer a stable candidate.',
  target_unverified: 'enforce declined: the target version could not be re-checked.',
  executor_not_claimed: 'Another writer’s rollback was already in progress.',
  executor_error: 'The rollback executor failed before redeploying.',
};

export const MODE_HELP: Record<string, string> = {
  monitor: 'A breach is recorded and paged, and the watch keeps polling to the end of its window. Never rolls back.',
  evaluate:
    'A breach at rca or rollback tier goes to the rollback RCA and rollback-decide, which records what enforce would have done.',
  enforce: 'As evaluate, and rollback-decide may roll the deploy back.',
};
